import React, { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Shield, FileText, PlayCircle, ChevronRight, ExternalLink, Mail, Phone } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactSection from '../components/ContactSection';
import { ManualGallery, VideoGallery } from '../components/SupportGalleries';
import { useSiteContent } from '../context/SiteContentContext';
import SEO from '../components/SEO';

const PAGE_BANNER = 'https://jilongot.com/public/upload/20240703/240F3163430613.jpg';
const iconMap = { Shield, FileText, PlayCircle };

// Map support-item id → anchor slug + rich extra content
const SECTION_MAP = {
  'sup-warranty': {
    anchor: 'warranty',
    highlights: [
      'Garansi resmi 2 tahun untuk semua fusion splicer & OTDR',
      'Layanan purna jual di service center Jakarta',
      'Sertifikat garansi digital pada setiap unit',
      'Klaim garansi mudah lewat form kontak / WhatsApp',
    ],
    cta: 'Ajukan Klaim Garansi',
    policy: {
      title: 'Kebijakan Garansi Resmi JILONG Indonesia',
      coverage: [
        { label: 'Masa Garansi', value: '24 bulan sejak tanggal pembelian (fusion splicer, OTDR, power meter, light source)' },
        { label: 'Aksesori', value: '6 bulan (adaptor, baterai, kabel, hard case)' },
        { label: 'Elektroda & V-groove', value: 'Sparepart konsumsi — tidak termasuk garansi' },
        { label: 'Cakupan', value: 'Cacat produksi, kegagalan komponen internal, dan kerusakan bukan karena pemakaian normal' },
      ],
      excluded: [
        'Kerusakan akibat jatuh, tekanan mekanis, atau tertimpa benda berat',
        'Kerusakan akibat air, cairan kimia, atau lingkungan ekstrem di luar spesifikasi',
        'Modifikasi hardware/firmware oleh pihak tidak resmi',
        'Kehilangan segel garansi atau nomor seri yang telah dihapus',
        'Kerusakan optik karena penggunaan connector kotor / tidak dibersihkan',
      ],
      steps: [
        'Isi form klaim garansi di halaman ini dengan model, S/N, dan foto/video kerusakan',
        'Tim service center akan memberikan tiket RMA dalam 1×24 jam kerja',
        'Kirim unit ke service center Jakarta dengan hardcase original',
        'Diagnosa gratis dalam 3–5 hari kerja + estimasi perbaikan bila di luar garansi',
        'Unit yang lolos klaim diperbaiki/diganti tanpa biaya (ongkos kirim ditanggung pembeli untuk pengiriman ke service)',
      ],
    },
  },
  'sup-manual': {
    anchor: 'manual',
    highlights: [
      'Manual PDF lengkap untuk model 300T, 260T, 280T, 500E, KL-series',
      'Panduan cepat (quick-start guide) tersedia dalam Bahasa Indonesia',
      'Firmware update guide & release notes',
      'Diagram sparepart & prosedur kalibrasi',
    ],
    cta: 'Unduh Manual Produk',
    gallery: 'manual',
  },
  'sup-tutorial': {
    anchor: 'tutorial',
    highlights: [
      'Tutorial step-by-step fusion splicing untuk teknisi baru',
      'Setup OTDR & interpretasi trace dengan contoh nyata',
      'Perawatan berkala fusion splicer (V-groove, elektroda, lensa)',
      'Troubleshooting error code umum',
    ],
    cta: 'Tonton di YouTube',
    gallery: 'video',
  },
};

const Breadcrumb = ({ items }) => (
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-wrap items-center gap-2 text-sm text-gray-600" data-testid="support-breadcrumb">
    {items.map((it, i) => (
      <React.Fragment key={i}>
        {i > 0 && <ChevronRight size={14} className="text-gray-400" />}
        {it.to ? (
          <Link to={it.to} className="hover:text-[#0a3d8f] transition-colors">{it.label}</Link>
        ) : (
          <span className="text-[#0a3d8f] font-semibold">{it.label}</span>
        )}
      </React.Fragment>
    ))}
  </div>
);

const WarrantyPolicyDetails = ({ policy }) => (
  <div className="mt-12 grid lg:grid-cols-3 gap-6" data-testid="warranty-policy-details">
    <div className="lg:col-span-3 bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
      <h3 className="text-xl font-bold text-gray-900 mb-5 font-[Montserrat]">{policy.title}</h3>
      <div className="grid sm:grid-cols-2 gap-3">
        {policy.coverage.map((c, i) => (
          <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-blue-50/60 border border-blue-100">
            <Shield size={18} className="text-[#0a3d8f] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-[#0a3d8f]">{c.label}</p>
              <p className="text-sm text-gray-700 leading-snug mt-0.5">{c.value}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
      <p className="text-xs font-bold uppercase tracking-wider text-red-600 mb-3">Tidak Termasuk Garansi</p>
      <ul className="space-y-2">
        {policy.excluded.map((e, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-gray-700 leading-relaxed">
            <span className="mt-2 inline-block w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" />
            <span>{e}</span>
          </li>
        ))}
      </ul>
    </div>
    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm lg:col-span-2">
      <p className="text-xs font-bold uppercase tracking-wider text-[#0a3d8f] mb-3">Cara Klaim Garansi</p>
      <ol className="space-y-3 counter-reset-[step]">
        {policy.steps.map((s, i) => (
          <li key={i} className="flex items-start gap-3 text-sm text-gray-700 leading-relaxed">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#0a3d8f] text-white text-xs font-bold flex items-center justify-center">
              {i + 1}
            </span>
            <span>{s}</span>
          </li>
        ))}
      </ol>
    </div>
  </div>
);

const SupportBlock = ({ item, reverse }) => {
  const Icon = iconMap[item.icon] || Shield;
  const extra = SECTION_MAP[item.id] || {};
  const anchor = extra.anchor || item.id;

  return (
    <section
      id={anchor}
      className={`py-14 lg:py-20 ${reverse ? 'bg-gradient-to-b from-blue-50/40 to-white' : 'bg-white'}`}
      data-testid={`support-block-${anchor}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`grid lg:grid-cols-2 gap-10 items-center ${reverse ? 'lg:[direction:rtl]' : ''}`}>
          <div className="lg:[direction:ltr]">
            <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#0a3d8f] to-[#0a8f3d] text-white flex items-center justify-center mb-5 shadow-lg">
              <Icon size={30} />
            </div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#0a3d8f] font-semibold mb-2">{item.title}</p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4 font-[Montserrat]">{item.titleId}</h2>
            <p className="text-gray-600 leading-relaxed mb-6">{item.description}</p>
            <ul className="space-y-2.5 mb-7">
              {(extra.highlights || []).map((h, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-gray-700">
                  <span className="mt-1.5 inline-block w-1.5 h-1.5 rounded-full bg-[#0a3d8f]" />
                  <span className="leading-relaxed">{h}</span>
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-3">
              {item.link && (
                <a
                  href={item.link}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 bg-[#0a3d8f] hover:bg-[#082e6b] text-white px-6 py-3 rounded-md text-sm font-semibold transition-colors"
                  data-testid={`support-cta-${anchor}`}
                >
                  {extra.cta || 'Buka Halaman'} <ExternalLink size={14} />
                </a>
              )}
              <a
                href="#contact"
                className="inline-flex items-center gap-2 border border-[#0a3d8f] text-[#0a3d8f] hover:bg-[#0a3d8f] hover:text-white px-6 py-3 rounded-md text-sm font-semibold transition-colors"
              >
                Hubungi Tim Support
              </a>
            </div>
          </div>
          <div className="lg:[direction:ltr]">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-gradient-to-br from-[#0a3d8f] to-[#082e6b] p-8 min-h-[280px] flex items-center justify-center">
              <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,white_1px,transparent_1px)] bg-[length:24px_24px]" />
              </div>
              <div className="relative z-10 text-white text-center">
                <Icon size={80} className="mx-auto mb-4 opacity-90" strokeWidth={1.3} />
                <p className="text-2xl font-bold font-[Montserrat] mb-1">{item.title}</p>
                <p className="text-sm text-white/70">{item.titleId}</p>
              </div>
            </div>
          </div>
        </div>

        {extra.policy && <WarrantyPolicyDetails policy={extra.policy} />}
        {extra.gallery === 'manual' && (
          <div className="mt-12"><ManualGallery /></div>
        )}
        {extra.gallery === 'video' && (
          <div className="mt-12"><VideoGallery /></div>
        )}
      </div>
    </section>
  );
};

const SupportPage = () => {
  const { supportItems, config } = useSiteContent();
  const location = useLocation();

  // Scroll to hash anchor once content loaded
  useEffect(() => {
    if (location.hash && supportItems && supportItems.length) {
      const id = location.hash.substring(1);
      const t = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 200);
      return () => clearTimeout(t);
    }
  }, [location.hash, supportItems]);

  return (
    <>
      <SEO path="/dukungan" pageName="Dukungan" />
      <Header />
      <main data-testid="support-page-root">
        <section className="relative h-[280px] md:h-[340px] overflow-hidden">
          <img src={PAGE_BANNER} alt="Support" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a3d8f]/85 via-[#0a3d8f]/60 to-transparent" />
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-center">
            <p className="text-white/80 text-sm uppercase tracking-[0.3em] mb-3">Support</p>
            <h1 className="text-white text-3xl md:text-5xl font-extrabold mb-3 font-[Montserrat]">Dukungan Teknis JILONG</h1>
            <p className="text-white/90 text-base md:text-lg max-w-2xl leading-relaxed">Layanan purna jual profesional, manual produk resmi, dan tutorial video langsung dari tim JILONG Indonesia.</p>
          </div>
        </section>

        <Breadcrumb items={[{ label: 'Home', to: '/' }, { label: 'Support' }]} />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6">
          <div className="flex flex-wrap gap-3">
            {(supportItems || []).map((s) => {
              const anchor = (SECTION_MAP[s.id] || {}).anchor || s.id;
              return (
                <a
                  key={s.id}
                  href={`#${anchor}`}
                  onClick={(e) => {
                    e.preventDefault();
                    const el = document.getElementById(anchor);
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-[#0a3d8f] hover:text-white hover:border-[#0a3d8f] transition-colors"
                  data-testid={`support-jump-${anchor}`}
                >
                  {s.title}
                </a>
              );
            })}
          </div>
        </div>

        {(supportItems || []).map((s, i) => (
          <SupportBlock key={s.id} item={s} reverse={i % 2 === 1} />
        ))}

        <section className="py-14 bg-gradient-to-r from-[#0a3d8f] to-[#082e6b] text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl md:text-3xl font-extrabold mb-3 font-[Montserrat]">Butuh Bantuan Cepat?</h3>
              <p className="text-white/85 leading-relaxed">Tim service center Jakarta siap membantu klaim garansi, permintaan manual, dan pertanyaan teknis lainnya.</p>
            </div>
            <div className="flex flex-wrap gap-3 md:justify-end">
              {config.phone && (
                <a href={`tel:${(config.phone || '').replace(/\s+/g, '')}`} className="inline-flex items-center gap-2 bg-white text-[#0a3d8f] hover:bg-white/90 px-5 py-3 rounded-md text-sm font-bold" data-testid="support-call">
                  <Phone size={16} /> {config.phone}
                </a>
              )}
              {config.email && (
                <a href={`mailto:${config.email}`} className="inline-flex items-center gap-2 border border-white/70 hover:bg-white/10 px-5 py-3 rounded-md text-sm font-bold" data-testid="support-email">
                  <Mail size={16} /> Email Support
                </a>
              )}
            </div>
          </div>
        </section>

        <ContactSection />
      </main>
      <Footer />
    </>
  );
};

export default SupportPage;
